'use client';
import { useControllableState } from '@radix-ui/react-use-controllable-state';
import { forwardRef, useRef, useState, useCallback, useEffect, useId } from 'react';
import { useComposedRefs } from '@radix-ui/react-compose-refs';
import { Slot } from '@radix-ui/react-slot';
import { flushSync } from 'react-dom';
import { Box, useTheme } from '@wanteddev/wds-engine';
import { composeEventHandlers } from '@radix-ui/primitive';
import { hideOthers } from '../../utils/index.mjs';
import RemoveScroll from '../remove-scroll/index.mjs';
import DismissableLayer from '../dismissable-layer/index.mjs';
import FocusScope from '../focus-scope/index.mjs';
import FlexBox from '../flex-box/index.mjs';
import IconButton from '../icon-button/index.mjs';
import ScrollArea from '../scroll-area/index.mjs';
import TextButton from '../text-button/index.mjs';
import Typography from '../typography/index.mjs';
import PortalOrFragment from '../portal-or-fragment/index.mjs';
import useResizeObserver from '../../hooks/use-resize-observer.mjs';
import { useModalContext, ModalContainerProvider, ModalActionAreaProvider, useModalContainerContext, useModalNavigationContext, useModalActionAreaContext, ModalNavigationProvider, ModalProvider } from './contexts.mjs';
import { MODAL_NAME, MODAL_CLOSE_NAME, MODAL_NAVIGATION_NAME, MODAL_NAVIGATION_ACTION_NAME } from './constants.mjs';
import { modalContainerWrapperStyle, modalDimmerStyle, modalContainerStyle, modalGrabberStyle, modalLeftIconStyle, modalRightIconStyle, modalNavigationStyle, modalNavigationWrapperStyle, modalNavigationTitleStyle, modalNavigationActionFloat, modalNavigationActionTextStyle, modalContentStyle, modalContentItemStyle } from './style.mjs';
import { useDraggable } from './hooks.mjs';
import { getDefaultCloseIcon } from './helpers.mjs';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';

var Modal = ({
  children,
  open: openProp,
  defaultOpen,
  onOpenChange,
  container,
  disableOutsideClickClose = false,
  disableEscapeKeyDownClose = false,
  disablePortal = false
}) => {
  const [open = false, setOpen] = useControllableState({
    prop: openProp,
    defaultProp: defaultOpen,
    onChange: onOpenChange
  });
  const containerRef = useRef(null);
  const innerContainerRef = useRef(null);
  return /* @__PURE__ */ jsx(
    ModalProvider,
    {
      containerRef,
      innerContainerRef,
      containerId: useId(),
      titleId: useId(),
      headingId: useId(),
      summaryId: useId(),
      descriptionId: useId(),
      open,
      disableOutsideClickClose,
      disableEscapeKeyDownClose,
      onOpenChange: setOpen,
      children: open && /* @__PURE__ */ jsx(Fragment, { children: /* @__PURE__ */ jsx(PortalOrFragment, { disablePortal, container, children: /* @__PURE__ */ jsx(Fragment, { children }) }) })
    }
  );
};
Modal.displayName = MODAL_NAME;
var ModalContainer = forwardRef(
  ({
    variant = "popup",
    size = "normal-fixed",
    handle,
    xs,
    sm,
    md,
    lg,
    xl,
    children,
    sticky = true,
    ...props
  }, ref) => {
    const {
      containerRef,
      disableOutsideClickClose,
      disableEscapeKeyDownClose,
      onOpenChange,
      ...context
    } = useModalContext(MODAL_NAME);
    const composedContainerRefs = useComposedRefs(ref, containerRef);
    const innerContainerRef = useRef(null);
    const composedInnerContainerRefs = useComposedRefs(
      innerContainerRef,
      context.innerContainerRef
    );
    const detectScrollRef = useRef(null);
    const [scrollHeight, setScrollHeight] = useState(0);
    const [hasScroll, setHasScroll] = useState(false);
    const handleOnScroll = useCallback(
      (e) => {
        const target = e.target;
        setScrollHeight(target.scrollTop);
      },
      [setScrollHeight]
    );
    const handleResize = useCallback(() => {
      const target = innerContainerRef.current;
      if (!target) {
        return;
      }
      setHasScroll(
        target.scrollHeight - target.clientHeight !== target.scrollTop
      );
    }, [setHasScroll]);
    useResizeObserver(detectScrollRef.current, handleResize);
    useEffect(() => {
      const target = innerContainerRef.current;
      if (!target) {
        return;
      }
      target.addEventListener("scroll", handleOnScroll);
      return () => target.removeEventListener("scroll", handleOnScroll);
    }, [handleOnScroll]);
    useEffect(() => {
      const content = containerRef.current;
      if (content) {
        return hideOthers(content);
      }
    }, []);
    const { isEnabled, ...dragProps } = useDraggable({
      variant,
      handle,
      xs,
      sm,
      md,
      lg,
      xl
    });
    const handleClose = useCallback(async () => {
      if (isEnabled && containerRef.current) {
        try {
          containerRef.current.style.setProperty(
            "--wds-modal-translate",
            "100%"
          );
          await containerRef.current.animate(
            [
              {
                transform: "translateY(0px)"
              },
              {
                transform: "translateY(100%)"
              }
            ],
            {
              duration: 200,
              easing: "ease"
            }
          ).finished;
          flushSync(() => {
            onOpenChange(false);
          });
        } catch (err) {
          onOpenChange(false);
        }
      } else {
        onOpenChange(false);
      }
    }, [onOpenChange, isEnabled, containerRef]);
    return /* @__PURE__ */ jsx(
      ModalContainerProvider,
      {
        scrollHeight,
        handleClose,
        children: /* @__PURE__ */ jsx(ModalActionAreaProvider, { sticky, hasScroll, children: /* @__PURE__ */ jsxs(
          Box,
          {
            sx: modalContainerWrapperStyle({
              variant,
              size,
              xs,
              sm,
              md,
              lg,
              xl
            }),
            children: [
              /* @__PURE__ */ jsx(RemoveScroll, { as: Slot, allowPinchZoom: true, shards: [containerRef], children: /* @__PURE__ */ jsx(
                Box,
                {
                  onPointerDown: (e) => {
                    const ctrlLeftClick = e.button === 0 && e.ctrlKey === true;
                    const isRightClick = e.button === 2 || ctrlLeftClick;
                    if (isRightClick || disableOutsideClickClose) {
                      e.preventDefault();
                      return;
                    }
                    handleClose();
                  },
                  sx: modalDimmerStyle
                }
              ) }),
              /* @__PURE__ */ jsx(FocusScope, { loop: true, trapped: context.open, children: /* @__PURE__ */ jsx(
                DismissableLayer,
                {
                  asChild: true,
                  onPointerDownOutside: (e) => {
                    e.preventDefault();
                  },
                  onFocusOutside: (e) => e.preventDefault(),
                  onEscapeKeyDown: (e) => {
                    if (disableEscapeKeyDownClose) {
                      e.preventDefault();
                    }
                  },
                  onDismiss: handleClose,
                  ref: composedContainerRefs,
                  children: /* @__PURE__ */ jsx(
                    Box,
                    {
                      role: "dialog",
                      "aria-modal": true,
                      id: context.containerId,
                      "aria-describedby": `${context.descriptionId} ${context.summaryId}`,
                      "aria-labelledby": `${context.titleId} ${context.headingId}`,
                      ...props,
                      sx: [
                        modalContainerStyle({
                          variant,
                          size,
                          xs,
                          sm,
                          md,
                          lg,
                          xl
                        }),
                        props.sx
                      ],
                      children: /* @__PURE__ */ jsx(
                        ScrollArea,
                        {
                          viewportRef: composedInnerContainerRefs,
                          sx: {
                            display: "flex",
                            flexGrow: "1"
                          },
                          viewPortProps: {
                            sx: {
                              height: "initial"
                            }
                          },
                          zIndex: 11,
                          children: /* @__PURE__ */ jsxs(FlexBox, { flexDirection: "column", ref: detectScrollRef, children: [
                            isEnabled && /* @__PURE__ */ jsx(
                              FlexBox,
                              {
                                justifyContent: "center",
                                sx: modalGrabberStyle,
                                ...dragProps
                              }
                            ),
                            children
                          ] })
                        }
                      )
                    }
                  )
                }
              ) })
            ]
          }
        ) })
      }
    );
  }
);
ModalContainer.displayName = "ModalContainer";
var ModalClose = forwardRef(
  ({ children, ...props }, ref) => {
    const { handleClose } = useModalContainerContext(MODAL_CLOSE_NAME);
    const { variant: navigationVariant } = useModalNavigationContext(MODAL_CLOSE_NAME);
    return /* @__PURE__ */ jsx(
      ModalNavigationAction,
      {
        ...props,
        onClick: composeEventHandlers(props.onClick, handleClose),
        ref,
        children: children ?? getDefaultCloseIcon(navigationVariant)
      }
    );
  }
);
ModalClose.displayName = MODAL_CLOSE_NAME;
var ModalNavigation = forwardRef(
  ({
    variant = "normal",
    leftButton,
    rightButton = /* @__PURE__ */ jsx(ModalClose, {}),
    toolbar,
    xs,
    sm,
    md,
    lg,
    xl,
    children,
    ...props
  }, ref) => {
    const context = useModalContext(MODAL_NAVIGATION_NAME);
    const { scrollHeight } = useModalContainerContext(MODAL_NAVIGATION_NAME);
    const { sticky = true } = useModalActionAreaContext() || {};
    const theme = useTheme();
    const leftButtonRedner = Boolean(leftButton) ? /* @__PURE__ */ jsx(FlexBox, { gap: "16px", sx: modalLeftIconStyle(variant), children: leftButton }) : null;
    const rightButtonRedner = Boolean(rightButton) && /* @__PURE__ */ jsx(FlexBox, { gap: "16px", sx: modalRightIconStyle(variant), children: rightButton });
    return /* @__PURE__ */ jsx(ModalNavigationProvider, { variant, children: /* @__PURE__ */ jsxs(
      FlexBox,
      {
        "wds-component": "modal-navigation",
        ref,
        flexDirection: "column",
        ...props,
        sx: [
          modalNavigationStyle({
            variant,
            xs,
            sm,
            md,
            lg,
            xl
          }),
          props.sx
        ],
        style: {
          ["--wds-navigation-border-color"]: sticky && scrollHeight > 0 ? theme.palette.line.normal.normal : "transparent",
          ...props.style
        },
        children: [
          /* @__PURE__ */ jsx(FlexBox, { sx: modalNavigationWrapperStyle(variant), children: variant !== "floating" ? /* @__PURE__ */ jsxs(Fragment, { children: [
            variant !== "extended" && leftButtonRedner,
            Boolean(children) && /* @__PURE__ */ jsx(
              FlexBox,
              {
                alignItems: "center",
                sx: modalNavigationTitleStyle(variant),
                "data-role": "navigation-title",
                children: /* @__PURE__ */ jsx(
                  Typography,
                  {
                    as: "h2",
                    id: context.titleId,
                    variant: "headline2",
                    weight: "bold",
                    color: "palette.label.strong",
                    display: "block",
                    sx: { margin: 0, border: "none" },
                    children
                  }
                )
              }
            ),
            variant !== "extended" ? rightButtonRedner : /* @__PURE__ */ jsxs(FlexBox, { sx: { width: "100%" }, children: [
              leftButtonRedner,
              rightButtonRedner
            ] })
          ] }) : /* @__PURE__ */ jsxs(Fragment, { children: [
            Boolean(leftButton) && /* @__PURE__ */ jsx(FlexBox, { gap: "16px", sx: modalLeftIconStyle(variant), children: leftButton }),
            Boolean(rightButton) && /* @__PURE__ */ jsx(FlexBox, { gap: "16px", sx: modalRightIconStyle(variant), children: rightButton })
          ] }) }),
          toolbar
        ]
      }
    ) });
  }
);
ModalNavigation.displayName = "ModalNavigation";
var ModalNavigationAction = forwardRef(
  ({
    children,
    variant = "icon",
    alternative,
    ...props
  }, ref) => {
    const id = useId();
    const { variant: navigationVariant } = useModalNavigationContext(
      MODAL_NAVIGATION_ACTION_NAME
    );
    if (variant === "icon") {
      return /* @__PURE__ */ jsx(
        IconButton,
        {
          variant: navigationVariant === "floating" ? "background" : "normal",
          size: 24,
          alternative,
          ...props,
          "wds-component": "modal-navigation-action",
          ref,
          children
        }
      );
    }
    if (navigationVariant === "floating") {
      return /* @__PURE__ */ jsx(
        IconButton,
        {
          variant: "background",
          size: 24,
          alternative,
          "aria-labelledby": id,
          ...props,
          sx: [modalNavigationActionFloat({ alternative }), props.sx],
          "wds-component": "modal-navigation-action",
          ref,
          children: /* @__PURE__ */ jsx(Typography, { as: "p", variant: "body2_normal", weight: "medium", id, children })
        }
      );
    }
    return /* @__PURE__ */ jsx(
      TextButton,
      {
        variant: "assistive",
        size: "medium",
        ...props,
        sx: [modalNavigationActionTextStyle, props.sx],
        "wds-component": "modal-navigation-action",
        ref,
        children
      }
    );
  }
);
ModalNavigationAction.displayName = MODAL_NAVIGATION_ACTION_NAME;
var ModalContent = forwardRef(
  ({
    padding,
    paddingExtra,
    paddingHeading,
    paddingInfo,
    xs,
    sm,
    md,
    lg,
    xl,
    ...props
  }, ref) => {
    return /* @__PURE__ */ jsx(
      Box,
      {
        sx: {
          height: "max-content",
          width: "fit-content",
          minWidth: "100%",
          flex: "1"
        },
        children: /* @__PURE__ */ jsx(
          FlexBox,
          {
            ref,
            as: "div",
            "wds-component": "modal-content",
            flexDirection: "column",
            ...props,
            sx: [
              modalContentStyle({
                padding,
                paddingExtra,
                paddingInfo,
                paddingHeading,
                xs,
                sm,
                md,
                lg,
                xl
              }),
              props.sx
            ]
          }
        )
      }
    );
  }
);
ModalContent.displayName = "ModalContent";
var ModalContentItem = forwardRef((props, ref) => {
  return /* @__PURE__ */ jsx(
    FlexBox,
    {
      ref,
      as: "div",
      gap: "12px",
      flexDirection: "column",
      ...props,
      sx: [modalContentItemStyle, props.sx]
    }
  );
});
ModalContentItem.displayName = "ModalContentItem";
var ModalHeading = forwardRef(
  ({ as, ...props }, ref) => {
    const context = useModalContext(MODAL_NAME);
    return /* @__PURE__ */ jsx(
      Typography,
      {
        ref,
        as: as || "h1",
        variant: "heading2",
        weight: "bold",
        color: "palette.label.normal",
        "data-role": "modal-heading",
        id: context.headingId,
        ...props
      }
    );
  }
);
ModalHeading.displayName = "ModalHeading";
var ModalSummary = forwardRef(
  ({ as, ...props }, ref) => {
    const context = useModalContext(MODAL_NAME);
    return /* @__PURE__ */ jsx(
      Typography,
      {
        ref,
        as: as || "p",
        variant: "body2_normal",
        weight: "regular",
        color: "palette.label.alternative",
        "data-role": "modal-summary",
        id: context.summaryId,
        ...props
      }
    );
  }
);
ModalSummary.displayName = "ModalSummary";
var ModalDescription = forwardRef(
  ({ as, ...props }, ref) => {
    const context = useModalContext(MODAL_NAME);
    return /* @__PURE__ */ jsx(
      Typography,
      {
        ref,
        as: as || "p",
        variant: "body1_reading",
        weight: "regular",
        color: "palette.label.normal",
        "data-role": "modal-description",
        id: context.descriptionId,
        ...props
      }
    );
  }
);
ModalDescription.displayName = "ModalDescription";

export { Modal, ModalClose, ModalContainer, ModalContent, ModalContentItem, ModalDescription, ModalHeading, ModalNavigation, ModalNavigationAction, ModalSummary };
